<script setup lang="ts">
/**
 * Search field for SelectContent (#header slot) or PopoverContent.
 * Matches CommandInput chrome (icon + border-b + flat input) and prevents
 * the select from closing when the user clicks or types in the filter.
 *
 * Must be placed in SelectContent's #header slot (outside the viewport) —
 * sticky-inside-viewport fails once Reka scrolls the selected item into view.
 * Also the canonical popover-list search header (Truto/Mongo/AI switcher).
 */
import { ref } from 'vue'
import { Search } from 'lucide-vue-next'
import { cn } from '@/lib/utils'

const props = withDefaults(defineProps<{
  modelValue: string
  placeholder?: string
  class?: string
  inputClass?: string
}>(), {
  placeholder: 'Search...',
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const inputEl = ref<HTMLInputElement | null>(null)

function onPointerDown(event: PointerEvent) {
  event.stopPropagation()
}

function onKeydown(event: KeyboardEvent) {
  event.stopPropagation()
  // First Escape clears the filter; a second Escape closes the select.
  if (event.key === 'Escape' && props.modelValue) {
    event.preventDefault()
    emit('update:modelValue', '')
  }
}

defineExpose({
  focus: () => inputEl.value?.focus(),
  blur: () => inputEl.value?.blur(),
})
</script>

<template>
  <div
    data-slot="select-search-header"
    :class="cn(
      'flex h-9 shrink-0 items-center gap-2 border-b bg-popover px-3',
      props.class,
    )"
    @pointerdown="onPointerDown"
  >
    <Search class="size-3.5 shrink-0 text-muted-foreground pointer-events-none" aria-hidden="true" />
    <input
      ref="inputEl"
      type="text"
      :value="modelValue"
      :placeholder="placeholder"
      autocomplete="off"
      autocorrect="off"
      spellcheck="false"
      :class="cn(
        'placeholder:text-muted-foreground flex h-full min-w-0 flex-1 bg-transparent text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50',
        inputClass,
      )"
      @input="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
      @keydown="onKeydown"
    >
  </div>
</template>
